/**
 * Hooks for fetching data from Hourly's API.
 *
 * These hooks call Hourly staging directly from the browser using the
 * manager's Hourly JWT (stored in AuthContext as `hourlyToken`).
 *
 * The underlying transport is the `hourlyApiClient` in `lib/api-client.ts`,
 * which injects the JWT via `registerHourlyTokenGetter`.
 */

import { useQuery } from "@tanstack/react-query";
import { hourlyClient } from "../../lib/hourly-orpc-client";

/**
 * A single 30-minute heatmap slot, expressed as staffing level deltas.
 * `supply` = number of employees scheduled for this slot.
 * `demand` = target headcount (currently estimated from the max supply across the week).
 * `closed` = true when no shifts exist for any slot in the day.
 */
export interface HeatmapSlot {
  demand: number;
  supply: number;
  closed: boolean;
}

/**
 * 7-day x 48-slot (30 min per slot) matrix derived from a schedule API response.
 * Outer array is days (0 = Monday), inner array is 48 half-hour slots (slot 0 = 00:00–00:30).
 */
export type HeatmapMatrix = HeatmapSlot[][];

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

const hourlyQueryKeys = {
  jobRoles: ["hourly", "jobRoles"] as const,
  locations: ["hourly", "locations"] as const,
  schedule: (locationId: string, startDate: string) =>
    ["hourly", "schedule", locationId, startDate] as const,
};

// ---------------------------------------------------------------------------
// useHourlyRoles — fetches active job roles from Hourly's GET /api/job-roles
// ---------------------------------------------------------------------------

/**
 * Fetches the list of job roles from Hourly for the authenticated manager's
 * company. Only active (non-archived) roles are returned by default.
 *
 * Requires the manager to be logged into Hourly (hourlyToken in AuthContext).
 * The token is injected by the `hourlyApiClient` via `registerHourlyTokenGetter`.
 *
 * @param enabled - set to false when no Hourly token is available
 */
export function useHourlyRoles(enabled: boolean = true) {
  return useQuery({
    queryKey: hourlyQueryKeys.jobRoles,
    queryFn: async () => {
      const roles = await hourlyClient.jobRole.getByCompany({});
      // Filter out archived roles on the client side for safety
      return roles.filter((r) => !r.isArchived);
    },
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes — roles don't change often
  });
}

// ---------------------------------------------------------------------------
// useHourlyLocations — fetches active locations from Hourly's GET /api/locations
// ---------------------------------------------------------------------------

/**
 * Fetches the list of locations from Hourly for the authenticated manager's
 * company. Only active locations are returned.
 *
 * Requires the manager to be logged into Hourly (hourlyToken in AuthContext).
 * The token is injected by the `hourlyApiClient` via `registerHourlyTokenGetter`.
 *
 * @param enabled - set to false when no Hourly token is available
 */
export function useHourlyLocations(enabled: boolean = true) {
  return useQuery({
    queryKey: hourlyQueryKeys.locations,
    queryFn: async () => {
      const locations = await hourlyClient.location.getLocations({ includeDeactivated: false });
      return locations;
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

// ---------------------------------------------------------------------------
// buildHeatmapMatrix — converts a ScheduleResponse into the 7x48 slot matrix
// ---------------------------------------------------------------------------

// Type inferred from the oRPC schedule contract
type ScheduleData = Awaited<ReturnType<typeof hourlyClient.schedule.getSchedule>>;

/**
 * Converts a Hourly schedule API response into the 7-day x 48-slot matrix
 * used by CoverageHeatmap.
 *
 * Supply per slot = number of employee shifts covering that 30-minute window.
 * Demand per slot = estimated from the peak supply observed across all slots
 * (a rough proxy when no demand forecast is available from Hourly).
 *
 * Days are aligned to the week containing `startDate` (Monday = day 0).
 */
export function buildHeatmapMatrix(schedule: ScheduleData): HeatmapMatrix {
  // Initialise 7 days x 48 half-hour slots with zero supply
  const supply: number[][] = Array.from({ length: 7 }, () => new Array<number>(48).fill(0));
  const hasAnyShift: boolean[] = new Array<boolean>(7).fill(false);

  // Collect all assigned shifts (employee shifts + open shifts)
  const allShifts = [
    ...schedule.locationEmployees.flatMap((emp) => emp.shifts),
    ...schedule.openShifts,
  ];

  const weekMondayMs = new Date(schedule.startDate).getTime();

  for (const shift of allShifts) {
    if (shift.isTimeOff) continue;

    const start = new Date(shift.shiftStart);
    const end = new Date(shift.shiftEnd);

    // Determine which day of the week this shift falls on (relative to Monday)
    const dayIndex = Math.floor((start.getTime() - weekMondayMs) / (24 * 60 * 60 * 1000));
    if (dayIndex < 0 || dayIndex >= 7) continue;

    hasAnyShift[dayIndex] = true;

    // Walk through each 30-minute slot the shift covers.
    // Use UTC hours/minutes to match the ISO timestamps in the schedule response.
    const startSlot = Math.floor((start.getUTCHours() * 60 + start.getUTCMinutes()) / 30);
    const endSlot = Math.ceil((end.getUTCHours() * 60 + end.getUTCMinutes()) / 30);

    for (let slot = startSlot; slot < endSlot && slot < 48; slot++) {
      supply[dayIndex][slot]++;
    }
  }

  // Estimate demand as the peak supply across the whole week
  const peakSupply = Math.max(...supply.flat(), 1);

  return supply.map((daySupply, dayIndex) =>
    daySupply.map((s, _slot) => ({
      supply: s,
      demand: peakSupply,
      closed: !hasAnyShift[dayIndex],
    })),
  );
}

// ---------------------------------------------------------------------------
// useHourlySchedule — fetches weekly schedule from Hourly's GET /api/schedule
// ---------------------------------------------------------------------------

/**
 * Fetches the weekly schedule for a specific Hourly location and week.
 * Returns raw schedule data plus a pre-computed heatmap matrix.
 *
 * `startDate` should be a Monday in ISO format (e.g. "2026-02-24T00:00:00.000Z").
 *
 * @param locationId - Hourly location UUID
 * @param startDate  - ISO datetime string for the start of the week (Monday)
 * @param enabled    - set to false when not ready to fetch
 */
export function useHourlySchedule(
  locationId: string | null,
  startDate: string,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: locationId
      ? hourlyQueryKeys.schedule(locationId, startDate)
      : (["hourly", "schedule", "none"] as const),
    queryFn: async () => {
      if (!locationId) throw new Error("locationId is required");
      const schedule = await hourlyClient.schedule.getSchedule({
        locationId,
        startDate,
        period: "WEEK",
      });
      return {
        schedule,
        heatmapMatrix: buildHeatmapMatrix(schedule),
      };
    },
    enabled: enabled && Boolean(locationId),
    staleTime: 2 * 60 * 1000, // 2 minutes — schedule changes more often
  });
}
